import {create} from 'zustand';
import {salesDashboardFactory} from './salesDashboardFactory';
import {InvoiceAttachmentDTO} from '../api/SalesDashboardAPIDataSource';

const NO_ATTACHMENT_MESSAGE_REGEX = /لا يوجد.*مرفق|لا يوجد ملف مرفق/i;

function isNoAttachmentResponse (e: { statusCode?: number; message?: string } | null): boolean {
  if (!e) return false;
  if (e.statusCode === 404) return true;
  return typeof e.message === 'string' && NO_ATTACHMENT_MESSAGE_REGEX.test(e.message);
}

interface SalesDashboardState {
  invoices: any[];
  pendingTransfers: any[];
  agingReport: any | null;
  loading: boolean;
  error: string | null;

  // Invoice Attachment State (scoped per invoice to avoid stale leak)
  invoiceAttachment: InvoiceAttachmentDTO | null;
  /** Which invoice the current invoiceAttachment belongs to; null when not loaded or cleared */
  attachmentInvoiceId: string | null;
  /** In-flight request: only apply response if it matches this invoice (race guard) */
  expectedAttachmentInvoiceId: string | null;
  attachmentLoading: boolean;
  attachmentError: string | null;

  fetchInvoicesWithFilters: (params: Record<string, any>) => Promise<void>;
  updateInvoiceStatus: (invoiceId: number | string, status: string) => Promise<void>;
  recordPayment: (payload: {invoice_id: number; amount: number; payment_method: string; notes?: string}) => Promise<void>;
  fetchPendingTransfers: (params?: {page?: number; limit?: number}) => Promise<void>;
  reviewBankTransfer: (payload: {invoice_id: number; action: 'approved'|'rejected'; notes?: string}) => Promise<void>;
  fetchAgingReport: (params?: {storeId?: number; dateFrom?: string; dateTo?: string}) => Promise<void>;
  
  // Invoice Attachment Actions
  fetchInvoiceAttachment: (invoiceId: string | number) => Promise<void>;
  /** Re-fetch attachment without clearing state (e.g. after upload); on failure does not set error. Returns attachment or null. */
  refreshInvoiceAttachment: (invoiceId: string | number) => Promise<InvoiceAttachmentDTO | null>;
  uploadInvoiceAttachment: (invoiceId: string | number, file: File) => Promise<InvoiceAttachmentDTO>;
  replaceInvoiceAttachment: (invoiceId: string | number, file: File) => Promise<InvoiceAttachmentDTO>;
  deleteInvoiceAttachment: (invoiceId: string | number) => Promise<void>;
  clearAttachmentError: () => void;
  /** Clear attachment state when modal closes or invoice changes (prevents stale leak) */
  clearInvoiceAttachment: () => void;
  
  clearError: () => void;
}

export const useSalesDashboardStore = create<SalesDashboardState>((set) => ({
  invoices: [],
  pendingTransfers: [],
  agingReport: null,
  loading: false,
  error: null,

  // Invoice Attachment State
  invoiceAttachment: null,
  attachmentInvoiceId: null,
  expectedAttachmentInvoiceId: null,
  attachmentLoading: false,
  attachmentError: null,

  async fetchInvoicesWithFilters (params) {
    set({loading: true, error: null});
    try {
      const res = await salesDashboardFactory.getInvoicesWithFilters().execute(params);
      set({invoices: res.data || [], loading: false});
    } catch (e: any) {
      set({error: e?.message || 'فشل جلب الفواتير', loading: false});
    }
  },

  async updateInvoiceStatus (invoiceId, status) {
    set({loading: true, error: null});
    try {
      await salesDashboardFactory.updateInvoiceStatus().execute(invoiceId, status);
      set({loading: false});
    } catch (e: any) {
      set({error: e?.message || 'فشل تحديث الحالة', loading: false});
    }
  },

  async recordPayment (payload) {
    set({loading: true, error: null});
    try {
      await salesDashboardFactory.recordPayment().execute(payload);
      set({loading: false});
    } catch (e: any) {
      set({error: e?.message || 'فشل تسجيل الدفعة', loading: false});
    }
  },

  async fetchPendingTransfers (params) {
    set({loading: true, error: null});
    try {
      const res = await salesDashboardFactory.getPendingBankTransfers().execute(params || {});
      set({pendingTransfers: res.data || [], loading: false});
    } catch (e: any) {
      set({error: e?.message || 'فشل جلب التحويلات', loading: false});
    }
  },

  async reviewBankTransfer (payload) {
    set({loading: true, error: null});
    try {
      await salesDashboardFactory.reviewBankTransfer().execute(payload);
      set({loading: false});
    } catch (e: any) {
      set({error: e?.message || 'فشل مراجعة التحويل', loading: false});
    }
  },

  async fetchAgingReport (params) {
    set({loading: true, error: null});
    try {
      const res = await salesDashboardFactory.getAgingReport().execute(params || {});
      set({agingReport: res.data || null, loading: false});
    } catch (e: any) {
      set({error: e?.message || 'فشل جلب تقرير الشيخوخة', loading: false});
    }
  },

  async fetchInvoiceAttachment (invoiceId) {
    const id = String(invoiceId);
    // Clear previous attachment so UI does not show stale data for another invoice
    set({
      attachmentLoading: true,
      attachmentError: null,
      invoiceAttachment: null,
      attachmentInvoiceId: null,
      expectedAttachmentInvoiceId: id
    });
    try {
      const attachment = await salesDashboardFactory.getInvoiceAttachment().execute(invoiceId);
      // Only apply response if it still matches the requested invoice (guard against race)
      set((state) => {
        if (state.expectedAttachmentInvoiceId !== id) return state;
        return {
          invoiceAttachment: attachment,
          attachmentInvoiceId: id,
          expectedAttachmentInvoiceId: null,
          attachmentLoading: false,
          attachmentError: null
        };
      });
    } catch (e: any) {
      if (isNoAttachmentResponse(e)) {
        set((state) => {
          if (state.expectedAttachmentInvoiceId !== id) return state;
          return {
            invoiceAttachment: null,
            attachmentInvoiceId: id,
            expectedAttachmentInvoiceId: null,
            attachmentLoading: false,
            attachmentError: null
          };
        });
      } else {
        set((state) => {
          if (state.expectedAttachmentInvoiceId !== id) return state;
          return {
            attachmentError: e?.message || 'حدث خطأ في تحميل معلومات المرفق',
            attachmentLoading: false,
            expectedAttachmentInvoiceId: null
          };
        });
      }
      throw e;
    }
  },

  async refreshInvoiceAttachment (invoiceId) {
    const id = String(invoiceId);
    set({attachmentLoading: true, attachmentError: null, expectedAttachmentInvoiceId: id});
    try {
      const attachment = await salesDashboardFactory.getInvoiceAttachment().execute(invoiceId);
      set((state) => {
        if (state.expectedAttachmentInvoiceId !== id) return state;
        return {
          invoiceAttachment: attachment,
          attachmentInvoiceId: id,
          expectedAttachmentInvoiceId: null,
          attachmentLoading: false,
          attachmentError: null
        };
      });
      return attachment;
    } catch {
      set((state) => {
        if (state.expectedAttachmentInvoiceId !== id) return state;
        return { attachmentLoading: false, expectedAttachmentInvoiceId: null };
      });
      return null;
    }
  },

  async uploadInvoiceAttachment (invoiceId, file) {
    const id = String(invoiceId);
    set({attachmentLoading: true, attachmentError: null});
    try {
      const attachment = await salesDashboardFactory.uploadInvoiceAttachment().execute(invoiceId, file);
      const valid = attachment && typeof attachment === 'object' && (attachment.download_url ?? (attachment as { file_url?: string }).file_url);
      if (valid) {
        set({invoiceAttachment: attachment, attachmentInvoiceId: id, attachmentLoading: false, attachmentError: null});
      } else {
        set({attachmentLoading: false, attachmentError: null});
      }
      return attachment;
    } catch (e: any) {
      set({attachmentError: e?.message || 'حدث خطأ في رفع المرفق', attachmentLoading: false});
      throw e;
    }
  },

  async replaceInvoiceAttachment (invoiceId, file) {
    const id = String(invoiceId);
    set({attachmentLoading: true, attachmentError: null});
    try {
      const attachment = await salesDashboardFactory.replaceInvoiceAttachment().execute(invoiceId, file);
      const hasUrl = attachment && typeof attachment === 'object' && (attachment.download_url ?? (attachment as { file_url?: string }).file_url);
      if (hasUrl) {
        set({invoiceAttachment: attachment, attachmentInvoiceId: id, attachmentLoading: false, attachmentError: null});
      } else {
        set({attachmentLoading: false, attachmentError: null});
      }
      return attachment;
    } catch (e: any) {
      set({attachmentError: e?.message || 'حدث خطأ في استبدال المرفق', attachmentLoading: false});
      throw e;
    }
  },

  async deleteInvoiceAttachment (invoiceId) {
    const id = String(invoiceId);
    set({attachmentLoading: true, attachmentError: null});
    try {
      await salesDashboardFactory.deleteInvoiceAttachment().execute(invoiceId);
      set({invoiceAttachment: null, attachmentInvoiceId: id, attachmentLoading: false, attachmentError: null});
    } catch (e: any) {
      set({attachmentError: e?.message || 'حدث خطأ في حذف المرفق', attachmentLoading: false});
      throw e;
    }
  },

  clearAttachmentError () { set({attachmentError: null}); },
  clearInvoiceAttachment () {
    set({
      invoiceAttachment: null,
      attachmentInvoiceId: null,
      expectedAttachmentInvoiceId: null,
      attachmentError: null,
      attachmentLoading: false
    });
  },
  clearError () { set({error: null}); }
}));
